import { useMemo } from "react";
import type { IntervieweeProfile } from "../../../domain/contracts";
import { incidentIconMap, incidentIconOrder } from "../model/incidentIconMap";
import {
  buildIncidentFamilyCounts,
  flattenIncidentCards,
  hasIncidentDetails,
  type IncidentDetailCardRecord,
  type IncidentFamily,
} from "../model/incidentTypes";

export type IntervieweeWitnessItem = {
  family: IncidentFamily;
  label: string;
  iconPath: string;
  count: number;
  sentence: string;
};

export type IntervieweeIncidentViewModel = {
  hasIncidentDetails: boolean;
  witnessItems: IntervieweeWitnessItem[];
  incidentCards: IncidentDetailCardRecord[];
};

function witnessSentence(family: IncidentFamily, count: number): string {
  const noun = count === 1 ? "incident" : "incidents";

  switch (family) {
    case "tortureOrRape":
      return `Witness reported ${count} torture/rape ${noun}.`;
    case "murdered":
      return `Witness reported ${count} murdered-person ${noun}.`;
    case "missingOrAbducted":
      return `Witness reported ${count} missing/abducted-person ${noun}.`;
    case "massGrave":
      return `Witness reported ${count} mass-grave ${noun}.`;
    default:
      return `Witness reported ${count} ${noun}.`;
  }
}

export function useIntervieweeIncidentViewModel(profile: IntervieweeProfile | null | undefined): IntervieweeIncidentViewModel {
  return useMemo(() => {
    const counts = buildIncidentFamilyCounts(profile);
    const cards = flattenIncidentCards(profile);

    const witnessItems = incidentIconOrder
      .map((family) => {
        const icon = incidentIconMap[family];
        const count = counts[family];

        return {
          family,
          label: icon.label,
          iconPath: icon.iconPath,
          count,
          sentence: witnessSentence(family, count),
        };
      })
      .filter((item) => item.count > 0);

    return {
      hasIncidentDetails: hasIncidentDetails(profile),
      witnessItems,
      incidentCards: cards,
    };
  }, [profile]);
}
